import { useQuery } from '@tanstack/react-query';
import { BookOpen, ExternalLink, Keyboard, PlayCircle } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { helpArticles, parseHelpArticle, type HelpArticle, type HelpKey } from '@/help';
import { Button, Sheet, SheetContent, Skeleton } from '@/components/ui';
import { docsUrl } from '@/lib/docs';
import { useAppStore } from '@/lib/store';
import { usePageMeta } from './nav';

// react-markdown + micromark only load when help is first opened
const HelpMarkdown = lazy(() => import('./HelpMarkdown'));

/** Loads and parses the help article for a key (cached for the session). */
export const useHelp = (key: HelpKey | null | undefined) =>
  useQuery<HelpArticle>({
    queryKey: ['help', key],
    queryFn: async () => parseHelpArticle(await helpArticles[key as HelpKey]()),
    enabled: Boolean(key),
    staleTime: Infinity,
    meta: { silent: true },
  });

/** Frontmatter `docsUrl` is a docs-site path (`guide/budgeting`), a `docs:` link or an absolute URL. */
const resolveDocs = (url: string | null): string | null => {
  if (!url) return null;
  if (/^https?:/.test(url)) return url;
  return docsUrl(url.replace(/^docs:/, ''));
};

/**
 * Contextual help side panel (`?`). Shows the article for the current route's `helpKey`, or one requested via
 * `openHelp(key)` (EmptyState "Learn more", `help:` links). Offers the route's tour and the full docs.
 */
export const HelpPanel = () => {
  const open = useAppStore((s) => s.helpOpen);
  const requested = useAppStore((s) => s.helpKey);
  const set = useAppStore((s) => s.set);
  const requestTour = useAppStore((s) => s.requestTour);
  const handle = usePageMeta();
  const key = requested ?? handle?.helpKey ?? 'home';
  const article = useHelp(open ? key : null);
  const docsHref = resolveDocs(article.data?.docsUrl ?? null);

  return (
    <Sheet open={open} onOpenChange={(o) => set({ helpOpen: o, ...(o ? {} : { helpKey: null }) })}>
      <SheetContent
        title={article.data?.title || handle?.title || 'Help'}
        description="Help for this page"
        footer={
          <div className="flex w-full flex-wrap items-center gap-2">
            {handle?.tourId && !requested ? (
              <Button variant="secondary" size="sm" icon={<PlayCircle aria-hidden />} onClick={requestTour}>
                Start tour
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" icon={<Keyboard aria-hidden />} onClick={() => set({ helpOpen: false, shortcutsOpen: true })}>
              Shortcuts
            </Button>
            {docsHref ? (
              <Button asChild variant="outline" size="sm" className="ml-auto">
                <a href={docsHref} target="_blank" rel="noreferrer">
                  <BookOpen aria-hidden />
                  Full guide
                  <ExternalLink aria-hidden />
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              </Button>
            ) : null}
          </div>
        }
      >
        {article.isPending ? (
          <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading help">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : article.isError ? (
          <p className="text-sm text-text-muted">Help for this page couldn't load. Try again, or open the full guide.</p>
        ) : (
          <Suspense fallback={<Skeleton className="h-40" />}>
            <HelpMarkdown body={article.data?.body ?? ''} onHelpLink={(k) => set({ helpKey: k })} />
          </Suspense>
        )}
      </SheetContent>
    </Sheet>
  );
};
